import React, { useMemo, useState } from "react";
import "./ActivityLog.css";

const ACTIVITY_LOGS = {
 featured: [
  {
    id: "featured-ceilang-case-study",
    date: "09,May",
    parts: [
      {
        text:
          "Added a detailed Ceilang case study exploring conversational language learning, immersive interactions, and emotionally engaging user experiences.",
      },
    ],
  },
  {
    id: "featured-portfolio-redesign",
    date: "07,May",
    parts: [
      {
        text:
          "Redesigned the portfolio website with cleaner storytelling, immersive layouts, stronger typography hierarchy, and improved project presentation.",
      },
    ],
  },
  {
    id: "featured-equora",
    date: "18,Apr",
    parts: [
      {
        text:
          "Added Equora, a stock market manager and AI analyzer focused on intelligent tracking, insights, and investment visualization.",
      },
    ],
  },
  {
    id: "featured-final-year-project",
    date: "28,Mar",
    parts: [
      {
        text:
          "Successfully completed the final year project after managing development cycles, documentation, testing phases, and academic presentations.",
      },
    ],
  },
  {
    id: "featured-nukepc-redesign",
    date: "22,Feb",
    parts: [
      {
        text:
          "Started freelance redesign work for the NukePC website focusing on modern layouts, conversion flow, and visual clarity.",
      },
    ],
  },
  {
    id: "featured-nukepc-internship",
    date: "14,Jan",
    parts: [
      {
        text:
          "Designed internship projects for NukePC including the mobile application experience and an internal CRM management platform.",
      },
    ],
  },
  {
    id: "featured-college-website",
    date: "06,Dec",
    parts: [
      {
        text:
          "Completed the college website project and continued maintenance work while balancing final year project responsibilities and submissions.",
      },
    ],
  },
  {
    id: "featured-old-portfolio",
    date: "27,Oct",
    parts: [
      {
        text:
          "Created the first version of my portfolio website to showcase freelance projects, UI experiments, and development work.",
      },
    ],
  },
],

  "case-study": [
    {
      id: "case-study-project-detail",
      date: "12,Apr",
      parts: [
        {
          text:
            "Designed the inner project reading layout with full-height sections, status-aware topics, and a Figma-style progress sidebar.",
        },
      ],
    },
    {
      id: "case-study-mobile-nav",
      date: "08,Apr",
      parts: [
        {
          text:
            "Added minimized and expanded mobile project navigation states so case studies stay readable on small screens.",
        },
      ],
    },
    {
      id: "case-study-progress",
      date: "01,Apr",
      parts: [
        {
          text:
            "Changed the project sidebar into a step tracker with completed, active, in-progress, and future states.",
        },
      ],
    },
  ],

  blogs: [
    {
      id: "blogs-first-log",
      date: "12,Apr",
      parts: [
        {
          text:
            "Started shaping the blogs section as a lightweight writing space for design notes, build logs, and experiments.",
        },
      ],
    },
    {
      id: "blogs-system",
      date: "08,Apr",
      parts: [
        {
          text:
            "Planned a reusable blog card structure with topics, reading states, and featured writing support.",
        },
      ],
    },
  ],
};

function RichText({ parts }) {
  return (
    <>
      {parts.map((part, index) => {
        if (part.type === "link") {
          return (
            <a
              key={`${part.text}-${index}`}
              href={part.href}
              target="_blank"
              rel="noreferrer"
            >
              {part.text}
            </a>
          );
        }

        return <React.Fragment key={index}>{part.text}</React.Fragment>;
      })}
    </>
  );
}

export default function ActivityLog({ page = "featured", logs }) {
  const [open, setOpen] = useState(false);

  const pageLogs = useMemo(() => {
    return logs || ACTIVITY_LOGS[page] || ACTIVITY_LOGS.featured;
  }, [logs, page]);

  const latest = pageLogs[0];

  if (!latest) return null;

  return (
    <div className={`activity-log-wrap ${open ? "is-open" : ""}`}>
      <section
        className={`activity-log ${open ? "is-open" : "is-minimized"}`}
        data-platform
      >
        <button
          className="activity-log__toggle"
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-label={open ? "Minimize activity logs" : "Expand activity logs"}
        >
          {open ? "−" : "+"}
        </button>

        {!open ? (
          <>
            <div className="activity-log__mini-head">
              <span>Latest log</span>
              <time>{latest.date}</time>
            </div>

            <div className="activity-log__mini-row">
              <span className="activity-log__heartbeat-dot" />
              <p>
                <RichText parts={latest.parts} />
              </p>
            </div>
          </>
        ) : (
          <>
            <h2 className="activity-log__title">Activity logs</h2>

            <div className="activity-log__scroll">
              <div className="activity-log__timeline">
                {pageLogs.map((log, index) => (
                  <article className="activity-log__entry" key={log.id}>
                    <time>{log.date}</time>

                    <span
                      className={`activity-log__timeline-dot ${
                        index === 0 ? "is-latest" : ""
                      }`}
                    />

                    {index < pageLogs.length - 1 && (
                      <span className="activity-log__timeline-line" />
                    )}

                    <p>
                      <RichText parts={log.parts} />
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
